import { type JSX, useState } from "react";

import { PaymentMethodModal } from "common/components/PaymentMethod";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { CARD_ICONS, PAYMENT_METHODS } from "common/constants";
import usePaymentSource from "common/datahooks/usePaymentSource";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

export default function SubscriptionPaymentMethod({
  subscriptionId,
  isCustomerSubscription,
}: {
  subscriptionId: string;
  isCustomerSubscription: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const { paymentSource, isLoadingPaymentSource } =
    usePaymentSource(subscriptionId);

  let content: JSX.Element;
  if (isLoadingPaymentSource) {
    content = (
      <>
        <Skeleton className="h-5 w-10" />
        <Skeleton className="h-5 w-24" />
      </>
    );
  } else if (paymentSource) {
    const Icon =
      paymentSource.type === "card"
        ? CARD_ICONS[paymentSource.card.brand]
        : PAYMENT_METHODS[paymentSource.type].icon;

    content = (
      <>
        <Icon className="border border-black-200 rounded-md" />

        <span className="text-sm-medium text-black-500">
          {paymentSource.type === "card"
            ? paymentSource.card.masked_number
            : paymentSource.paypal.email}
        </span>
      </>
    );
  } else {
    content = (
      <span className="text-sm-medium text-black-500">
        No credit card added
      </span>
    );
  }

  const editButtonComponent = (
    <Button
      variant="link-purple"
      size={isDesktop ? "sm" : "md"}
      className="ml-auto"
      onClick={() => setIsOpen(true)}
      disabled={isLoadingPaymentSource}
    >
      Edit
    </Button>
  );

  return (
    <>
      <div className="flex items-center gap-x-1">
        {content}

        {isCustomerSubscription ? (
          editButtonComponent
        ) : (
          <RestrictedComponent visibleForPermissions={["billing_full"]}>
            {editButtonComponent}
          </RestrictedComponent>
        )}
      </div>

      <PaymentMethodModal
        subscriptionId={subscriptionId}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        paymentSource={paymentSource}
      />
    </>
  );
}
